import { useEffect } from "react";
import { Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import DataGrid from "../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import { dateFormat } from "../../core/utils/date-utils";
import {
    billingSourceKey,
    billingStatusColor,
    billingStatusKey,
} from "../../enums/ReportBillingStatusEnum";

/**
 * Bir raporun faturalandırma geçmişi. Kaynak, ortak `logs` koleksiyonu değil, faturalandırmanın
 * kendi `report_billing_logs` koleksiyonudur; bu yüzden alanlar tipli gelir (eski/yeni durum,
 * batch, kaynak) ve JSON kazımaya gerek kalmaz.
 *
 * batchId verilirse rapor geçmişi yerine "bu toplu işlemde ne oldu" dökümünü gösterir.
 */
export default function ReportBillingLogsGrid({ reportId, batchId }) {
    const { t } = useTranslation();
    const { setDataGridProps, queryModel, setData, setPaginationResult } = useDataGrid();

    const initDataGrid = async () => {
        const response = batchId
            ? await ApiService.ReportBilling.GetBatchLogs(batchId, queryModel)
            : await ApiService.ReportBilling.GetLogs(reportId, queryModel);

        if (response.success) {
            setData(response.data);
            setPaginationResult(response.pagination);
        }
    }

    useEffect(() => {
        initDataGrid();
    }, [queryModel.updateKey, reportId, batchId]);

    useEffect(() => {
        setDataGridProps({
            rowKey: 'id',
            enableRowSelection: false,
            antdTableProps: { scroll: {} },
            columns: [
                {
                    title: t('Date'),
                    dataIndex: 'createdDate',
                    key: 'createdDate',
                    width: 140,
                    render: (text) => dateFormat(text)
                },
                {
                    title: t('Old Status'),
                    dataIndex: 'oldStatus',
                    key: 'oldStatus',
                    width: 130,
                    render: (text) => (
                        <Tag color={billingStatusColor(text)}>{t(billingStatusKey(text))}</Tag>
                    )
                },
                {
                    title: t('New Status'),
                    dataIndex: 'newStatus',
                    key: 'newStatus',
                    width: 130,
                    render: (text) => (
                        <Tag color={billingStatusColor(text)}>{t(billingStatusKey(text))}</Tag>
                    )
                },
                {
                    title: t('User'),
                    dataIndex: 'userName',
                    key: 'userName',
                    width: 160,
                    // Kullanıcı "guid__GörünenAd" biçiminde saklanıyor (mevcut log biçimi).
                    render: (text) => text?.split('__')?.[1] || '-'
                },
                {
                    title: t('Source'),
                    dataIndex: 'source',
                    key: 'source',
                    width: 120,
                    render: (text, record) => (
                        <span>
                            {t(billingSourceKey(text))}
                            {record.isBulk && <Tag style={{ marginLeft: 4 }}>{t('Bulk')}</Tag>}
                        </span>
                    )
                },
                ...(batchId ? [{
                    title: 'Id',
                    dataIndex: 'reportId',
                    key: 'reportId',
                    width: 90,
                }] : []),
                {
                    title: t('Description'),
                    dataIndex: 'description',
                    key: 'description',
                    render: (text, record) => (
                        <Typography.Text ellipsis={{ tooltip: text || record.fileName }}>
                            {text || record.fileName || '-'}
                        </Typography.Text>
                    )
                },
            ]
        })
    }, [batchId]);

    return (
        <DataGrid name={'report-billing-logs'} />
    )
}
